import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import { FaLock, FaCheck, FaEye, FaFileDownload } from "react-icons/fa";
import CreditAuctionProposalsPagination from "../CreditAuctionProposalsPage/credit-auction-proposals-pagination";
import {
  EDGE_COLUMN_PX,
  tableCellStyles,
  tableHeaderStyles,
  tagButtonStyle,
} from "../FinancialShared/tableStyles";
import {
  bloquearConsultasInfoLote,
  ConsultasInfoLoteAtivoItem,
  ConsultasInfoLoteInativoItem,
  gerarRelatorioConsultasInfoLote,
  getConsultasInfoLoteAtivo,
  getConsultasInfoLoteInativoByCorbanId,
} from "@/services/consignado/consume-quotes-corban.service";
import { useDebounce } from "@/utils/useDebounce";

// Converte uma data (ISO/Date do backend) para DD/MM/YYYY,
// formato esperado pelo endpoint de relatório.
const dateToBrDate = (value: string | Date): string => {
  const d = new Date(value);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${d.getFullYear()}`;
};

type ConsumoLoteTableProps = {
  searchTerm?: string;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
};

const INATIVO_DEFAULT_PAGE_SIZE = 10;

export default function ConsumoLoteTable({
  searchTerm = "",
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: ConsumoLoteTableProps) {
  const backgroundStyle = useConditionalBackground();
  const toast = useToast();

  const debouncedSearch = useDebounce(searchTerm, 500);

  const [ativos, setAtivos] = useState<ConsultasInfoLoteAtivoItem[]>([]);
  const [totalAtivos, setTotalAtivos] = useState(0);
  const [totalPagesAtivos, setTotalPagesAtivos] = useState(1);
  const [isLoadingAtivos, setIsLoadingAtivos] = useState(false);
  const [isErrorAtivos, setIsErrorAtivos] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selected, setSelected] = useState<ConsultasInfoLoteAtivoItem | undefined>(
    undefined,
  );
  const [inativos, setInativos] = useState<ConsultasInfoLoteInativoItem[]>([]);
  const [inativosPage, setInativosPage] = useState(1);
  const [inativosPageSize, setInativosPageSize] = useState(
    INATIVO_DEFAULT_PAGE_SIZE,
  );
  const [inativosTotalPages, setInativosTotalPages] = useState(1);
  const [inativosTotal, setInativosTotal] = useState(0);
  const [loadingInativos, setLoadingInativos] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [generatingReportId, setGeneratingReportId] = useState<string | null>(
    null,
  );

  const fetchAtivos = useCallback(async () => {
    setIsLoadingAtivos(true);
    setIsErrorAtivos(false);
    try {
      const response = await getConsultasInfoLoteAtivo(
        page,
        pageSize,
        debouncedSearch,
      );
      setAtivos(response.data ?? []);
      setTotalAtivos(response.total ?? 0);
      setTotalPagesAtivos(Math.max(1, response.totalPages ?? 1));
    } catch (error) {
      console.error("Erro ao buscar consumo ativo por lote:", error);
      setIsErrorAtivos(true);
      setAtivos([]);
    } finally {
      setIsLoadingAtivos(false);
    }
  }, [page, pageSize, debouncedSearch]);

  useEffect(() => {
    fetchAtivos();
  }, [fetchAtivos]);

  useEffect(() => {
    if (page > totalPagesAtivos) {
      onPageChange(1);
    }
  }, [totalPagesAtivos, page, onPageChange]);

  const showNomeCorbanCol = useMemo(
    () => ativos.some((item) => !!item.nomeCorban?.trim()),
    [ativos],
  );
  const showNomePromotoraCol = useMemo(
    () => ativos.some((item) => !!item.nomePromotora?.trim()),
    [ativos],
  );

  const fetchInativos = useCallback(
    async (corbanId: number, pageInativo: number, sizeInativo: number) => {
      setLoadingInativos(true);
      try {
        const response = await getConsultasInfoLoteInativoByCorbanId(
          corbanId,
          pageInativo,
          sizeInativo,
        );
        setInativos(response.data ?? []);
        setInativosTotal(response.total ?? 0);
        setInativosTotalPages(Math.max(1, response.totalPages ?? 1));
      } catch (error) {
        console.error("Erro ao buscar consumo inativo por lote:", error);
        setInativos([]);
        setInativosTotal(0);
        setInativosTotalPages(1);
        toast({
          title: "Erro ao carregar histórico de consumo",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      } finally {
        setLoadingInativos(false);
      }
    },
    [toast],
  );

  const handleVisualizarConsumo = (item: ConsultasInfoLoteAtivoItem) => {
    setSelected(item);
    setInativosPage(1);
    setInativosPageSize(INATIVO_DEFAULT_PAGE_SIZE);
    onOpen();
    fetchInativos(item.corbanId, 1, INATIVO_DEFAULT_PAGE_SIZE);
  };

  const handleSetBloqueio = async (
    item: ConsultasInfoLoteAtivoItem,
    bloqueado: boolean,
  ) => {
    setTogglingId(item.id);
    try {
      await bloquearConsultasInfoLote(item.id, bloqueado);
      toast({
        title: bloqueado
          ? `Consumo bloqueado para corban ${item.corbanId}`
          : `Consumo liberado para corban ${item.corbanId}`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      await fetchAtivos();
    } catch (error) {
      console.error("Erro ao alterar bloqueio:", error);
      toast({
        title: "Erro ao alterar bloqueio",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setTogglingId(null);
    }
  };

  const handleGerarRelatorio = async (item: ConsultasInfoLoteAtivoItem) => {
    // Data início é fixa: a própria data de criação exibida na linha.
    // Data fim não é enviada — o backend sempre considera a data atual.
    setGeneratingReportId(item.id);
    try {
      const fileUrl = await gerarRelatorioConsultasInfoLote(item.corbanId, {
        dataInicio: dateToBrDate(item.dataCriacao),
      });
      window.open(fileUrl, "_blank", "noopener,noreferrer");
      toast({
        title: `Relatório do corban ${item.corbanId} gerado com sucesso`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Erro ao gerar relatório:", error);
      toast({
        title: "Erro ao gerar relatório",
        description: "Não foi possível gerar o arquivo. Tente novamente.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setGeneratingReportId(null);
    }
  };

  return (
    <>
      {isLoadingAtivos ? (
        <Flex justify="center" align="center" minH="240px">
          <Spinner size="xl" color="teal.500" />
        </Flex>
      ) : isErrorAtivos ? (
        <Flex direction="column" justify="center" align="center" minH="240px" gap={4}>
          <Text color="red.500">Erro ao carregar consumos por lote.</Text>
          <Button onClick={fetchAtivos} colorScheme="teal" size="sm">
            Tentar novamente
          </Button>
        </Flex>
      ) : ativos.length !== 0 ? (
        <Box bg="white" borderRadius="xl" boxShadow="sm" px={0} py={{ base: 2, md: 4 }}>
          <CreditAuctionProposalsPagination
            currentPage={page}
            totalPages={totalPagesAtivos}
            pageSize={pageSize}
            totalItems={totalAtivos}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />

          <TableContainer borderRadius="lg" overflowX="auto">
            <Table variant="simple">
              <Thead {...backgroundStyle}>
                <Tr>
                  <Th {...tableHeaderStyles} px={EDGE_COLUMN_PX} w="200px">
                    AÇÕES
                  </Th>
                  <Th {...tableHeaderStyles}>ID CORBAN</Th>
                  {showNomeCorbanCol && (
                    <Th {...tableHeaderStyles}>NOME CORBAN</Th>
                  )}
                  {showNomePromotoraCol && (
                    <Th {...tableHeaderStyles}>NOME PROMOTORA</Th>
                  )}
                  <Th {...tableHeaderStyles}>DATA CRIAÇÃO</Th>
                  <Th {...tableHeaderStyles}>CONSULTAS EM LOTE</Th>
                  <Th {...tableHeaderStyles} px={EDGE_COLUMN_PX}>
                    STATUS
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {ativos.map((item) => (
                  <Tr key={item.id}>
                    <Td {...tableCellStyles} px={EDGE_COLUMN_PX}>
                      <SimpleGrid columns={2} spacing={1}>
                        <Tooltip
                          label={
                            item.consumoBloqueado
                              ? "Liberar Consumo"
                              : "Consumo já liberado"
                          }
                          placement="top"
                        >
                          <Button
                            leftIcon={<FaCheck />}
                            size="xs"
                            {...tagButtonStyle("green")}
                            fontFamily="Lexend Deca"
                            fontWeight="normal"
                            px={2}
                            onClick={() => handleSetBloqueio(item, false)}
                            isDisabled={
                              !item.consumoBloqueado || togglingId === item.id
                            }
                            isLoading={togglingId === item.id}
                          >
                            Liberar
                          </Button>
                        </Tooltip>
                        <Tooltip
                          label={
                            item.consumoBloqueado
                              ? "Consumo já bloqueado"
                              : "Bloquear Consumo"
                          }
                          placement="top"
                        >
                          <Button
                            leftIcon={<FaLock />}
                            size="xs"
                            {...tagButtonStyle("red")}
                            fontFamily="Lexend Deca"
                            fontWeight="normal"
                            px={2}
                            onClick={() => handleSetBloqueio(item, true)}
                            isDisabled={
                              item.consumoBloqueado || togglingId === item.id
                            }
                            isLoading={togglingId === item.id}
                          >
                            Bloquear
                          </Button>
                        </Tooltip>
                        <Tooltip label="Visualizar Histórico" placement="top">
                          <Button
                            leftIcon={<FaEye />}
                            size="xs"
                            {...tagButtonStyle("blue")}
                            fontFamily="Lexend Deca"
                            fontWeight="normal"
                            px={2}
                            onClick={() => handleVisualizarConsumo(item)}
                          >
                            Histórico
                          </Button>
                        </Tooltip>
                        <Tooltip label="Baixar Relatório" placement="top">
                          <Button
                            leftIcon={<FaFileDownload />}
                            size="xs"
                            {...tagButtonStyle("teal")}
                            fontFamily="Lexend Deca"
                            fontWeight="normal"
                            px={2}
                            onClick={() => handleGerarRelatorio(item)}
                            isDisabled={generatingReportId === item.id}
                            isLoading={generatingReportId === item.id}
                          >
                            Relatório
                          </Button>
                        </Tooltip>
                      </SimpleGrid>
                    </Td>
                    <Td {...tableCellStyles} color="blue.500">
                      {item.corbanId}
                    </Td>
                    {showNomeCorbanCol && (
                      <Td {...tableCellStyles}>{item.nomeCorban ?? "-"}</Td>
                    )}
                    {showNomePromotoraCol && (
                      <Td {...tableCellStyles}>{item.nomePromotora ?? "-"}</Td>
                    )}
                    <Td {...tableCellStyles}>
                      {item.dataCriacao
                        ? new Date(item.dataCriacao).toLocaleDateString("pt-BR")
                        : "-"}
                    </Td>
                    <Td {...tableCellStyles}>{item.numeroConsulta ?? 0}</Td>
                    <Td {...tableCellStyles} px={EDGE_COLUMN_PX}>
                      <Badge
                        colorScheme={item.consumoBloqueado ? "red" : "green"}
                        borderRadius="md"
                        px={2}
                        py={1}
                        fontFamily="Lexend Deca"
                        fontSize="xs"
                        fontWeight="normal"
                      >
                        {item.consumoBloqueado ? "Bloqueada" : "Ativa"}
                      </Badge>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>

          <CreditAuctionProposalsPagination
            currentPage={page}
            totalPages={totalPagesAtivos}
            pageSize={pageSize}
            totalItems={totalAtivos}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </Box>
      ) : (
        <Heading mt={8} textAlign="center" color="gray.600">
          {debouncedSearch.trim()
            ? "Nenhum corban encontrado para a busca informada"
            : "Não há corbans para exibir"}
        </Heading>
      )}

      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent maxW="1100px" maxH="90vh">
          <ModalHeader fontFamily="Lexend Deca">
            Histórico de Consumo por Lote - Corban {selected?.corbanId}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto" maxH="calc(90vh - 140px)">
            {loadingInativos ? (
              <Flex justify="center" align="center" minH="200px">
                <Spinner size="xl" color="teal.500" />
              </Flex>
            ) : inativos.length > 0 ? (
              <>
                <CreditAuctionProposalsPagination
                  currentPage={inativosPage}
                  totalPages={inativosTotalPages}
                  pageSize={inativosPageSize}
                  totalItems={inativosTotal}
                  onPageChange={(p) => {
                    setInativosPage(p);
                    if (selected)
                      fetchInativos(selected.corbanId, p, inativosPageSize);
                  }}
                  onPageSizeChange={(size) => {
                    setInativosPageSize(size);
                    setInativosPage(1);
                    if (selected) fetchInativos(selected.corbanId, 1, size);
                  }}
                />
                <TableContainer borderRadius="lg" overflowX="auto">
                  <Table variant="simple">
                    <Thead {...backgroundStyle}>
                      <Tr>
                        <Th {...tableHeaderStyles} px={EDGE_COLUMN_PX}>
                          DATA CRIAÇÃO
                        </Th>
                        <Th {...tableHeaderStyles}>CONSULTAS EM LOTE</Th>
                        <Th {...tableHeaderStyles}>BLOQUEADA</Th>
                        <Th {...tableHeaderStyles} px={EDGE_COLUMN_PX}>
                          ÚLTIMA ATUALIZAÇÃO
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {inativos.map((item) => (
                        <Tr key={item.id}>
                          <Td {...tableCellStyles} px={EDGE_COLUMN_PX}>
                            {item.dataCriacao
                              ? new Date(item.dataCriacao).toLocaleDateString(
                                  "pt-BR",
                                )
                              : "-"}
                          </Td>
                          <Td {...tableCellStyles}>{item.numeroConsulta ?? 0}</Td>
                          <Td {...tableCellStyles}>
                            {item.consumoBloqueado ? "Sim" : "Não"}
                          </Td>
                          <Td {...tableCellStyles} px={EDGE_COLUMN_PX}>
                            {item.updatedAt
                              ? new Date(item.updatedAt).toLocaleDateString(
                                  "pt-BR",
                                )
                              : "-"}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              </>
            ) : (
              <Text textAlign="center" color="gray.500" py={10}>
                Nenhum histórico de consumo encontrado para este corban.
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Fechar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
